import { useEffect, useRef, useState } from 'react';
import type { PortalDog, PortalStay } from '@bbbk/schema';
import { CARD_CURRENT_CODE, CARD_PAUSED_CODE, CARD_PENDING_CODE } from '@bbbk/schema';
import { api, ApiError } from '../lib/api';
import { Avatar, DisclosureRegion } from '../components/common';
import { ProofFull, isPdf, proofImageUrl } from '../components/bookings/ProofImage';
import { PendingButton } from '../components/common/PendingButton';
import { G } from './glyphs';
import { Tag, type TagTone } from './Tag';
import { LeaveDialog } from './LeaveDialog';
import { BookBrand } from './RequestChrome';
import { MessagePatricia } from './MessagePatricia';
import { ProofWell, Thumb } from './ProofWell';
import { VaxRow } from './RegistrationDogs';
import { initialOf } from './RegistrationDetails';
import { cardNeed, cardOf, type CardNeed } from './homeRules';
import { dogLine, maskComplete, maskToVaccineDue, monthYearLabel, vaccineDueToMask } from './registrationRules';
import { uploadLockout } from './PaySheet';
import { scrollToTopOf } from '../lib/scrollTop';

// THE DOGS DRAWER — flow 2.5's *Your dogs*, in the app's one drawer over Home
// (vaccination-cards AC1–AC3, AC8–AC10; Freddi, 2026-10-10: "once the
// vaccination button clicked it should open a drawer with the your dogs page
// contents visible"; 2026-10-13: "the vaccination card upload flow should
// follow the same procedure as deposit proof - using drawer rather than its own
// page").
//
// THE PAY SHEET'S WAY: its own URL (`/dogs`) over Home, Back closes it, X asks
// when a card is attached, sent closes it and Home says so — unless another
// dog on the stay still needs a card, when the drawer stays and that dog opens
// next (AC1b). The page's body, never its chrome: no title bar, no tab bar
// (deviation 19). Every dog is a row; the one Home named is open.
//
// One card, one shape: shut it is a row with its tag; open it is the editor —
// the well, the two dates and Send — or, once sent, the same card locked
// (`c4w`), accepted (`c4ok`), or with Patricia (AC9). The rule is the server's
// (`customerMayUpload` via `cardNeed`), so the drawer never offers what the
// endpoint will refuse.

export interface DogsSheetDraft {
  file: File | null;
  /** A file refused before it left the phone. */
  problem: string | null;
  /** `MM/YY` as typed. */
  validUntil: string;
  kcUntil: string;
}
export const EMPTY_DRAFT: DogsSheetDraft = { file: null, problem: null, validUntil: '', kcUntil: '' };

/**
 * A CARD TURNED AWAY IS NOT SENT (PR #260 review): the drawer was out of date — the card already with
 * Patricia, over to her, or accepted for the stay. The drawer stays open and says so; Home re-reads
 * behind it. Null for any other refusal, which the drawer words as it always has.
 */
export function cardRefusal(code: string | undefined): string | null {
  if (code === CARD_PENDING_CODE) return 'Patricia already has this card — she’ll answer it first.';
  if (code === CARD_PAUSED_CODE) return 'Patricia will sort this card out with you — message her.';
  if (code === CARD_CURRENT_CODE) return 'Patricia has already accepted this card — it covers the stay.';
  return null;
}

/**
 * EACH DOG KEEPS ITS OWN DRAFT (PR #260 review): opening another dog put the photo and dates just
 * given for this one in the bin, with no leave question. The draft being left is kept against its
 * dog; the one being opened is its kept draft, or `fresh`.
 */
export function carryDrafts(stash: Readonly<Record<string, DogsSheetDraft>>, fromId: string | null, draft: DogsSheetDraft, toId: string | null, fresh: DogsSheetDraft): { stash: Record<string, DogsSheetDraft>; draft: DogsSheetDraft } {
  const next = { ...stash };
  if (fromId) next[fromId] = draft;
  const kept = toId ? next[toId] : undefined;
  if (toId) delete next[toId];
  return { stash: next, draft: kept ?? fresh };
}

/** A card the customer may send now: none, out of date, or refused (`customerMayUpload`'s answer, read from /me). */
const sendable = (need: CardNeed | null) => need === 'ask' || need === 'refused';

/**
 * A card the drawer offers to take: one asked for, or — with no card on file and none with Patricia —
 * one for a dog already in date (Freddi, 2026-10-13: "there should be an empty state visible even when
 * your vaccines are in date"). The server takes it: it refuses only while a card waits or is paused.
 */
const takesCard = (need: CardNeed | null, onFile: boolean) => sendable(need) || (!onFile && need !== 'checking' && need !== 'paused');

/** The draft a dog's row opens with: an in-date dog's own dates already typed, so the card is all it asks. */
export function draftFor(dog: PortalDog | undefined, today: string, checkIn: string | null): DogsSheetDraft {
  if (!dog || dog.card.review || cardNeed(dog, today, checkIn) !== null) return EMPTY_DRAFT;
  return { ...EMPTY_DRAFT, validUntil: vaccineDueToMask(dog.vaccinesDue), kcUntil: vaccineDueToMask(dog.kcDue) };
}

const onStay = (dogs: readonly PortalDog[], stay: PortalStay | null) => (stay ? dogs.filter((d) => stay.dogIds.includes(d.id)) : dogs);

/** Which row opens: the dog Home named, else the first on the stay still needing a card, else the first. */
export function dogsSheetOpen(dogs: readonly PortalDog[], wanted: string | null, stay: PortalStay | null, today: string): string | null {
  if (wanted && dogs.some((d) => d.id === wanted)) return wanted;
  const outstanding = onStay(dogs, stay).find((d) => sendable(cardNeed(d, today, stay?.checkIn ?? null)));
  return outstanding?.id ?? dogs[0]?.id ?? null;
}

/** After a send: the next dog on the stay still needing a card, or null — and the drawer closes (AC1b). */
export function nextOutstanding(dogs: readonly PortalDog[], sentId: string, stay: PortalStay | null, today: string): string | null {
  return onStay(dogs, stay).find((d) => d.id !== sentId && sendable(cardNeed(d, today, stay?.checkIn ?? null)))?.id ?? null;
}

const label = (due: string | null | undefined) => monthYearLabel(due ?? null) ?? null;

/** The shut row's tag: the card's date in its state's tone (`c4p`), or the state the answer left it in. */
function tagOf(dog: PortalDog, sent: boolean, today: string, checkIn: string | null): { tone: TagTone; text: string } {
  const state = sent ? 'checking' : dog.card.review?.state;
  if (state === 'checking') return { tone: 'wait', text: 'Checking' };
  if (state === 'paused') return { tone: 'wait', text: 'With Patricia' };
  if (state === 'not_accepted') return { tone: 'bad', text: 'Not accepted' };
  const c = cardOf(dog, today, checkIn);
  if (c.state === 'none' && !c.until) return { tone: 'neutral', text: 'No card' };
  return { tone: c.state === 'ok' ? 'ok' : c.state === 'due' ? 'due' : 'bad', text: c.until ?? 'No card' };
}

export interface DogsSheetViewProps {
  dogs: readonly PortalDog[];
  /** The next stay — what the dates are judged against; null with none booked. */
  stay: PortalStay | null;
  today: string;
  contactName: string;
  openId: string | null;
  /** The open dog's card, chosen and typed on this phone. */
  draft: DogsSheetDraft;
  sending: boolean;
  failure: string | null;
  /** Dogs whose card went up in this drawer — drawn checking at once, before the re-read lands. */
  marked: readonly string[];
  /** The file each of those dogs sent, so its thumbnail is the card itself until the re-read brings its id. */
  sentFiles?: Readonly<Record<string, File>>;
  onOpen: (id: string | null) => void;
  onFile: (file: File | null, problem: string | null) => void;
  onDates: (patch: Partial<Pick<DogsSheetDraft, 'validUntil' | 'kcUntil'>>) => void;
  onSend: () => void;
  onClose: () => void;
}

export function DogsSheetView({ dogs, stay, today, contactName: _contactName, openId, draft, sending, failure, marked, sentFiles = {}, onOpen, onFile, onDates, onSend, onClose }: DogsSheetViewProps) {
  const page = useRef<HTMLElement>(null);
  useEffect(() => { scrollToTopOf(page.current); }, []);
  // THEIR OWN CARD, OPENED (Freddi, 2026-10-13): the one they sent, full screen, through the image
  // route that serves the owning household — never a drawing of a card.
  const [viewing, setViewing] = useState<{ id: string; mime: string } | null>(null);
  const checkIn = stay?.checkIn ?? null;
  const ready = !!draft.file && maskComplete(draft.validUntil) && maskComplete(draft.kcUntil);

  const row = (dog: PortalDog) => {
    const open = dog.id === openId;
    const sent = marked.includes(dog.id);
    const tag = tagOf(dog, sent, today, checkIn);
    const need = sent ? 'checking' : cardNeed(dog, today, checkIn);
    const review = sent ? null : dog.card.review ?? null;
    const accepted = review?.state === 'accepted';
    const hasCard = review !== null || sent;
    const locked = !takesCard(need, hasCard);
    const shown = {
      validUntil: (accepted ? dog.vaccinesDue : dog.card.validUntil) ?? dog.vaccinesDue ?? '',
      kcUntil: (accepted ? dog.kcDue : dog.card.kcUntil) ?? dog.kcDue ?? '',
    };
    const proof = !sent && dog.card.proofId ? { id: dog.card.proofId, mime: dog.card.mime ?? '' } : null;
    const thumb = proof && !isPdf(proof.mime) ? (
      <button type="button" className="cp-thumb has-img" aria-label="Open your card" onClick={() => setViewing(proof)}><img src={proofImageUrl(proof.id)} alt="" /></button>
    ) : proof ? (
      <button type="button" className="cp-thumb is-pdf" aria-label="Open your card" onClick={() => setViewing(proof)}>{G.clipboard({ size: 16 })}</button>
    ) : <Thumb file={sentFiles[dog.id] ?? null} />;
    const body = locked ? (
      <>
        {need !== 'paused' && hasCard && (
          <div className="cp-upload dc-has up-locked">
            {thumb}
            <div className="dc-file"><b>{review?.byDesk ? 'Card from Patricia' : 'Your card'}</b><small>{sent ? 'Sent just now' : accepted ? 'Accepted · tap to view' : 'Sent · tap to view'}</small></div>
            <span className="dc-done">{G.tick({ size: 14 })}</span>
          </div>
        )}
        {need !== 'paused' && (
          <>
            <VaxRow label="Annual vaccination" name={`valid-${dog.id}`} value={shown.validUntil} editable={false} today={today} onChange={() => {}} />
            <VaxRow label="Kennel cough" name={`kc-${dog.id}`} value={shown.kcUntil} editable={false} today={today} onChange={() => {}} />
          </>
        )}
        <p className="dc-note">
          {G.info({ size: 13 })}
          <span>
            {need === 'paused'
              ? 'With Patricia — she’ll sort this card out with you.'
              : !hasCard
                ? 'In date for the stay — nothing needed.'
                : accepted
                  ? `${review?.byDesk ? 'Patricia added this card. ' : ''}Patricia has accepted your card. Nothing else needed before the stay.`
                  : `${review?.byDesk ? 'Patricia added this card. ' : ''}Patricia has your card. She’ll get back to you if she needs anything else.`}
          </span>
        </p>
        {/* The one way out while it is hers (AC2, AC9): her thread, nothing typed (Freddi, 2026-10-13). */}
        {(need === 'checking' || need === 'paused') && <div className="dc-acts"><MessagePatricia /></div>}
      </>
    ) : (
      <>
        {need === 'refused' && (
          <div className="cp-card msg msg-stop pay-refused">
            <span className="msg-ic">{G.warn({ size: 15 })}</span>
            <div className="msg-txt"><b>Patricia couldn’t accept the last card</b><small>Send a clear photo of the up-to-date card.</small></div>
          </div>
        )}
        <ProofWell inputId={`card-${dog.id}`} file={open ? draft.file : null} problem={open ? draft.problem : null} sending={sending} title="Add the card" sub="A photo or a PDF" refused="That card wasn’t accepted" onFile={onFile} />
        <VaxRow required label="Annual vaccination" name={`valid-${dog.id}`} value={open ? draft.validUntil : ''} editable today={today} onChange={(v) => onDates({ validUntil: v })} />
        <VaxRow required label="Kennel cough" name={`kc-${dog.id}`} value={open ? draft.kcUntil : ''} editable today={today} onChange={(v) => onDates({ kcUntil: v })} />
        <p className="dc-note">{G.info({ size: 13 })}<span>{need === null ? 'In date — add the card so Patricia has it on file.' : 'Patricia will check your card and get back to you if she needs anything else.'}</span></p>
        {open && failure && <div className="cp-card msg msg-stop pay-fail"><span className="msg-ic">{G.warn({ size: 15 })}</span><div className="msg-txt"><b>{failure}</b></div></div>}
        <div className="dc-acts">
          <PendingButton className="cp-btn primary" icon={G.clipboard({ size: 15 })} onClick={onSend} busy={open && sending} disabled={!open || !ready || sending}>Send the card</PendingButton>
        </div>
      </>
    );
    // ONE CARD, ONE HEAD, SHUT OR OPEN (Freddi, 2026-10-13: "opening and closing the cards should not
    // move the header … at all"): the head is the same element in both states — only the chevron
    // turns — and the body folds beneath it through the app's one slide.
    return (
      <div key={dog.id} className={'cp-dogcard dc-fold' + (open ? ' open' : '')}>
        <button type="button" className="dc-head-btn" aria-expanded={open} aria-label={`${open ? 'Fold' : 'Open'} ${dog.name}`} onClick={() => onOpen(open ? null : dog.id)}>
          <div className="dc-head">
            <Avatar name={dog.name} id={dog.id} size={38} kind="dog" initials={initialOf(dog.name)} />
            <div className="dc-id"><b>{dog.name}</b><small>{dogLine(dog)}</small></div>
            {/* The date with the vaccination mark beside it (Freddi, 2026-10-13). */}
            <Tag tone={tag.tone} glyph={G.syringe({ size: 11 })}>{tag.text}</Tag>
            <span className={'dc-chev' + (open ? ' is-open' : '')}>{G.chevron({ size: 15 })}</span>
          </div>
        </button>
        <DisclosureRegion open={open}>
          <div className={'dc-body dc-vax' + (locked ? ' is-locked' : '')}>
            <div className="dc-vax-h">
              <span className="dc-vax-ic">{G.syringe({ size: 14 })}</span>
              <b>Vaccinations</b>
            </div>
            {body}
          </div>
        </DisclosureRegion>
      </div>
    );
  };

  return (
    <main className="cp-app cp-wizard cp-book cp-dogs" ref={page}>
      <div className="cp-scroll cp-step">
        <BookBrand onClose={onClose} busy={sending} />
        <h1 className="cp-h1">Your dogs</h1>
        <div className="cp-dogs-list">{dogs.map(row)}</div>
      </div>
      {viewing && <ProofFull id={viewing.id} mime={viewing.mime} alt="Your vaccination card" onClose={() => setViewing(null)} />}
    </main>
  );
}

export interface DogsSheetProps {
  dogs: readonly PortalDog[];
  stay: PortalStay | null;
  today: string;
  contactName: string;
  /** The row Home named, from `?dog=`. */
  wanted: string | null;
  /** A card went up — `next` is the dog to open, or null when the drawer should close. */
  onSent: (dogId: string, next: string | null) => void;
  /** Left without sending. */
  onLeave: () => void;
  closeRef?: React.MutableRefObject<(() => void) | null>;
  onLockedOut?: (why: 'signed-out' | 'no-household') => void;
  /** A card was turned away because the drawer was out of date — read Home again behind it. */
  onStale?: () => void;
}

export function DogsSheet({ dogs, stay, today, contactName, wanted, onSent, onLeave, closeRef, onLockedOut, onStale }: DogsSheetProps) {
  const [openId, setOpenId] = useState<string | null>(() => dogsSheetOpen(dogs, wanted, stay, today));
  const checkIn = stay?.checkIn ?? null;
  const [draft, setDraft] = useState<DogsSheetDraft>(() => draftFor(dogs.find((d) => d.id === openId), today, checkIn));
  const [sending, setSending] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [marked, setMarked] = useState<string[]>([]);
  const [sentFiles, setSentFiles] = useState<Record<string, File>>({});
  const [leaving, setLeaving] = useState(false);
  const [kept, setKept] = useState<Record<string, DogsSheetDraft>>({});
  const open = (id: string | null) => {
    if (sending) return;
    const moved = carryDrafts(kept, openId, draft, id, draftFor(dogs.find((d) => d.id === id), today, checkIn));
    setKept(moved.stash); setOpenId(id); setDraft(moved.draft); setFailure(null);
  };
  const send = async () => {
    const dogId = openId;
    if (!dogId || !draft.file || sending) return;
    setSending(true); setFailure(null);
    const form = new FormData();
    form.set('file', draft.file);
    form.set('valid_until', maskToVaccineDue(draft.validUntil));
    form.set('kc_until', maskToVaccineDue(draft.kcUntil));
    try {
      await api(`/portal/dogs/${encodeURIComponent(dogId)}/card`, { method: 'POST', form, redirectOn401: false });
      const next = nextOutstanding(dogs.filter((d) => !marked.includes(d.id)), dogId, stay, today);
      setMarked((m) => [...m, dogId]);
      setSentFiles((f) => ({ ...f, [dogId]: draft.file! }));
      setDraft(draftFor(dogs.find((d) => d.id === next), today, checkIn));
      if (next) setOpenId(next);
      onSent(dogId, next);
    } catch (e) {
      const locked = uploadLockout(e);
      if (locked && onLockedOut) { onLockedOut(locked); return; }
      // Already with Patricia, over to her, or accepted for the stay: what the drawer shows is out of
      // date. Say so where the card is, keep the drawer, and let the re-read redraw it — never Card sent.
      const stale = e instanceof ApiError ? cardRefusal(e.code) : null;
      if (stale) { setFailure(stale); onStale?.(); return; }
      setFailure(e instanceof ApiError ? (e.status >= 500 ? 'Something went wrong on our side — your card is still on your phone. Try again.' : e.message || 'That card wasn’t accepted.') : 'That didn’t reach us — your card is still on your phone. Try again.');
    } finally {
      setSending(false);
    }
  };
  const close = () => { if (sending) return; draft.file || Object.values(kept).some((d) => d.file) ? setLeaving(true) : onLeave(); };
  if (closeRef) closeRef.current = close;
  return (
    <>
      <DogsSheetView dogs={dogs} stay={stay} today={today} contactName={contactName} openId={openId} draft={draft} sending={sending} failure={failure} marked={marked} sentFiles={sentFiles}
        onOpen={open} onFile={(file, problem) => { setDraft((d) => ({ ...d, file, problem })); setFailure(null); }}
        onDates={(patch) => setDraft((d) => ({ ...d, ...patch }))} onSend={() => void send()} onClose={close} />
      {leaving && <LeaveDialog what="card" onStay={() => setLeaving(false)} onLeave={onLeave} />}
    </>
  );
}
